export const CANONICAL_DOMAIN = 'https://sevenbpo.com.br';

export type SeoPageCategory =
  | 'home'
  | 'pillar_service'
  | 'sub_service'
  | 'segment'
  | 'institutional'
  | 'platform'
  | 'blog_index'
  | 'blog_post'
  | 'contact';

export interface SeoBreadcrumbItem {
  label: string;
  path: string;
}

export interface SeoFaqItem {
  question: string;
  answer: string;
}

export interface SeoProcessStep {
  stepNumber: string;
  title: string;
  description: string;
  slaOrDeliverable?: string;
}

export interface SeoPainPoint {
  symptom: string;
  rootCause: string;
  sevenSolution: string;
}

export interface SeoKpiMetric {
  value: string;
  label: string;
  context: string;
}

export interface SeoPageDefinition {
  id: string;
  path: string;
  category: SeoPageCategory;
  sitemapPriority: number;
  changefreq: 'daily' | 'weekly' | 'monthly';
  metaTitle: string;
  metaDescription: string;
  keywords: string[];
  breadcrumbs: SeoBreadcrumbItem[];
  heroTag: string;
  h1: string;
  leadParagraph: string;
  targetSectionId?: string;
  geoSummaryBlock: {
    entityDefinition: string;
    targetAudience: string;
    coreDeliverables: string[];
    methodologySummary: string;
  };
  primaryCtaLabel: string;
  whatsappPrefillMessage: string;
  faqs: SeoFaqItem[];
  relatedPaths: string[];
}

export interface SeoServicePage extends SeoPageDefinition {
  category: 'home' | 'pillar_service' | 'sub_service' | 'platform' | 'institutional';
  shortTitle: string;
  slug?: string;
  iconName: string;
  serviceScope: {
    includedActivities: string[];
    excludedActivities: string[];
    erpAndBankIntegrations: string[];
  };
  processSteps: SeoProcessStep[];
  deliverables: {
    title: string;
    frequency: string;
    description: string;
  }[];
  kpis: SeoKpiMetric[];
  comparisonRows?: {
    criterion: string;
    internalTeamCostOrRisk: string;
    accountingOnlyScope: string;
    sevenBpoAdvantage: string;
  }[];
}

export interface SeoSegmentPage extends SeoPageDefinition {
  category: 'segment';
  slug: string;
  segmentName: string;
  shortDescription: string;
  iconName: string;
  sectorPainPoints: SeoPainPoint[];
  specificRoutines: {
    title: string;
    description: string;
  }[];
  sectorIndicators: SeoKpiMetric[];
  recommendedServicePaths: string[];
}

export interface SeoBlogPost extends SeoPageDefinition {
  category: 'blog_post';
  slug: string;
  categoryLabel: string;
  publishedAt: string;
  updatedAt: string;
  readingTimeMinutes: number;
  authorName: string;
  authorRole: string;
  excerpt: string;
  sections: {
    id: string;
    heading: string;
    paragraphs: string[];
    bulletPoints?: string[];
  }[];
}

/* ============================================================================
   CONTEÚDO ORIGEM DA LANDING PAGE (100% FIEL AO LANDINGPAGE.TSX)
   ============================================================================ */

export const ORIGINAL_LANDING_CONTENT = {
  heroTag: 'A plataforma financeira que trabalha com você',
  h1: 'Menos planilha. Mais decisão — no seu tempo.',
  subheadline:
    'A Seven organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real. Você para de passar o dia amarrado no operacional — e volta a decidir com clareza. Para empresas, contadores e escritórios que cuidam de vários clientes.',
  heroHighlights: [
    'Conciliação em minutos',
    'Multi-empresa, em um só painel',
    'Resultado em tempo real',
    'Vários clientes, um só acesso',
  ],
  trustedBrands: ['CGM', 'NEXUS', 'MediaAlvo', 'Claves', 'Genesis', 'Imobdesk'],
  platform: {
    tag: 'TUDO NO MESMO LUGAR',
    heading: 'O financeiro completo, sem quebra-cabeça.',
    lead: 'Você lança uma vez e a Seven cuida do resto. O que você digita hoje aparece na conciliação, alimenta os relatórios e chega no resultado do mês — sem exportar planilha, sem retrabalho, sem perder o fio.',
    features: [
      {
        number: '01',
        title: 'Você enxerga o caixa em tempo real',
        desc: "Entradas, saídas, saldo e o que ainda falta receber — atualizados no momento em que acontecem. Fim do 'espera fechar o mês pra saber'.",
      },
      {
        number: '02',
        title: 'Lançar deixa de ser tarefa chata',
        desc: 'Cadastro em segundos, comprovante anexado com um arraste e as contas do mês que se repetem sozinhas. Menos digitação, mais tempo pra pensar.',
      },
      {
        number: '03',
        title: 'A conciliação anda sozinha',
        desc: 'O extrato do banco chega, o assistente sugere a correspondência e você só confirma. O que tomava a manhã, agora leva minutos.',
      },
      {
        number: '04',
        title: 'O resultado do mês pronto quando você abrir',
        desc: 'Receita, custo, lucro e comparativo com o que foi previsto — em uma tela clara, pronta pra apresentar pro cliente ou pro sócio.',
      },
    ],
  },
  intelligence: {
    heading: 'Uma assistente que responde na hora — em português.',
    lead: 'Conheça a Lity. Pergunte como se estivesse falando com um colega — e receba a resposta na hora, direto dos números da sua empresa. Sem exportar planilha, sem esperar relatório.',
    items: [
      {
        title: 'Pergunte como quiser',
        desc: '"Quais foram meus maiores gastos em junho?" — a Lity entende e responde na hora.',
      },
      {
        title: 'Enxergue sua carteira num piscar',
        desc: '"Quantos clientes ativos eu tenho?" com o corte por status e receita, sem montar relatório.',
      },
      {
        title: 'Nada passa batido',
        desc: 'Mostra o que está aberto, atrasado ou esperando você — antes do cliente cobrar.',
      },
      {
        title: 'Categoriza pra você',
        desc: 'Sugere onde cada lançamento se encaixa e mantém tudo no lugar certo, sem você pensar.',
      },
      {
        title: 'Fala a sua língua do tempo',
        desc: 'Entende "hoje", "esta semana", "mês passado" — sem precisar montar filtro.',
      },
      {
        title: 'Fecha o mês no seu ritmo',
        desc: 'Sugere as correspondências do extrato e transforma horas de fechamento em minutos.',
      },
      {
        title: 'Resolve várias linhas de uma vez',
        desc: 'Categoriza dezenas de transações em um só clique — chega de arrastar linha por linha.',
      },
      {
        title: 'Transforma extrato em lançamento',
        desc: 'Escolhe a linha do extrato, ela vira lançamento no sistema. Simples assim.',
      },
    ],
  },
  operation: {
    tag: 'Toda a sua carteira',
    heading: 'Todos os seus clientes. Um único acesso.',
    lead: 'Troque de empresa em um clique. Cada uma no seu espaço, sem confusão, sem retrabalho — e a Seven trabalhando por baixo enquanto você foca no cliente.',
    stats: [
      { value: '+30', label: 'Empresas no mesmo painel', context: 'Gestão multi-empresa em um só lugar' },
      { value: '1', label: 'Login = carteira inteira', context: 'Vários clientes, um só acesso' },
      { value: '100%', label: 'Dados protegidos e isolados', context: 'Cada empresa no seu espaço' },
      { value: '24/7', label: 'Trabalho rodando pra você', context: 'Enquanto você foca no cliente' },
    ],
    routines: [
      { stepNumber: '01', title: 'Conciliações do dia', description: 'Conciliações do dia realizadas sem você lembrar', slaOrDeliverable: 'realizadas' },
      { stepNumber: '02', title: 'Extrato do banco', description: 'Extrato do banco conciliado chega sozinho', slaOrDeliverable: 'conciliado' },
      { stepNumber: '03', title: 'Pagamento recebido', description: 'Pagamento recebido baixado no mesmo instante', slaOrDeliverable: 'baixado' },
      { stepNumber: '04', title: 'Novo cliente', description: 'Novo cliente cadastrado só com o CNPJ', slaOrDeliverable: 'cadastrado' },
      { stepNumber: '05', title: 'Endereço completo', description: 'Endereço completo preenchido só com o CEP', slaOrDeliverable: 'preenchido' },
      { stepNumber: '06', title: 'Contas recorrentes', description: 'Contas recorrentes lançadas todo mês, sozinhas', slaOrDeliverable: 'lançadas' },
    ],
  },
  contrast: {
    lessHeading: 'A rotina amarrada no operacional',
    less: [
      'noites de fechamento',
      'planilha que ninguém entende',
      'cliente cobrando resposta',
      'achismo virando decisão',
      'um login pra cada empresa',
    ],
    moreHeading: 'Clareza para crescer',
    more: [
      'fechamento em minutos',
      'resposta na hora',
      'cliente confiando no dado',
      'decisão com números reais',
      'carteira inteira em um lugar',
    ],
  },
  bpoEssencial: {
    tag: 'SOLUÇÃO PERSONALIZADA',
    heading: 'BPO FINANCEIRO',
    lead: 'Organize e impulsione seu departamento financeiro com a excelência e assessoria da Seven Analytics.',
    bullets: [
      'Contas a pagar e receber de forma organizada',
      'Gestão completa de cobranças e pagamentos',
      'Conciliações diárias e relatórios de desempenho',
      'Análise de fluxo de caixa projetado para tomada de decisão',
      'E muito mais para escalar seu negócio...',
    ],
    customProposalNote:
      'Como cada empresa possui uma demanda de volume, transações e complexidade diferente, a equipe da Seven Analytics monta uma proposta personalizada sob medida para a sua operação.',
    ctaHeading: 'Pronto para profissionalizar sua gestão financeira com a Seven Analytics?',
    ctaDescription:
      'Agende um diagnóstico com nossos consultores especialistas e receba um plano de atendimento exclusivo para o seu modelo de negócio.',
  },
  cta: {
    tag: 'PRONTO PARA COMEÇAR?',
    heading: 'Seu financeiro estratégico, a partir de hoje.',
    lead: 'Fale com um de nossos especialistas e veja como a Seven Analytics pode organizar, automatizar e gerar inteligência para a sua operação.',
  },
} as const;

const COMMON_FAQS: SeoFaqItem[] = [
  {
    question: 'O que está incluso no BPO Financeiro da Seven Analytics?',
    answer:
      'Organize e impulsione seu departamento financeiro com a excelência e assessoria da Seven Analytics: contas a pagar e receber de forma organizada, gestão completa de cobranças e pagamentos, conciliações diárias e relatórios de desempenho, e análise de fluxo de caixa projetado para tomada de decisão.',
  },
  {
    question: 'Como funciona a plataforma da Seven Analytics no dia a dia?',
    answer:
      'Você lança uma vez e a Seven cuida do resto. O que você digita hoje aparece na conciliação, alimenta os relatórios e chega no resultado do mês — sem exportar planilha, sem retrabalho, sem perder o fio.',
  },
  {
    question: 'O que a assistente Lity faz na plataforma?',
    answer:
      'Pergunte como se estivesse falando com um colega e receba a resposta na hora, direto dos números da sua empresa. A Lity categoriza lançamentos, sugere correspondências do extrato e mostra o que está aberto ou atrasado.',
  },
  {
    question: 'Como funciona a proposta comercial de BPO Financeiro?',
    answer:
      'Como cada empresa possui uma demanda de volume, transações e complexidade diferente, a equipe da Seven Analytics monta uma proposta personalizada sob medida para a sua operação.',
  },
];

const COMMON_GEO_SUMMARY = {
  entityDefinition:
    'A Seven organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real. Você para de passar o dia amarrado no operacional — e volta a decidir com clareza.',
  targetAudience:
    'Para empresas, contadores e escritórios que cuidam de vários clientes.',
  coreDeliverables: [...ORIGINAL_LANDING_CONTENT.bpoEssencial.bullets],
  methodologySummary: ORIGINAL_LANDING_CONTENT.platform.lead,
};

const COMMON_SCOPE = {
  includedActivities: [...ORIGINAL_LANDING_CONTENT.bpoEssencial.bullets],
  excludedActivities: [...ORIGINAL_LANDING_CONTENT.contrast.less],
  erpAndBankIntegrations: [...ORIGINAL_LANDING_CONTENT.heroHighlights],
};

const COMMON_DELIVERABLES = ORIGINAL_LANDING_CONTENT.platform.features.map((f) => ({
  title: f.title,
  frequency: 'Tempo real',
  description: f.desc,
}));

const COMMON_KPIS: SeoKpiMetric[] = ORIGINAL_LANDING_CONTENT.operation.stats.map((s) => ({
  value: s.value,
  label: s.label,
  context: s.context,
}));

const COMMON_COMPARISON_ROWS = ORIGINAL_LANDING_CONTENT.contrast.less.map((lessItem, idx) => ({
  criterion: `0${idx + 1}`,
  internalTeamCostOrRisk: lessItem,
  accountingOnlyScope: lessItem,
  sevenBpoAdvantage: ORIGINAL_LANDING_CONTENT.contrast.more[idx] || '',
}));

/* ============================================================================
   1. HOME & PÁGINAS DE SERVIÇO
   ============================================================================ */

export const HOME_SEO_METADATA: SeoServicePage = {
  id: 'home',
  path: '/',
  category: 'home',
  shortTitle: 'Início',
  iconName: 'BarChart3',
  targetSectionId: 'top',
  sitemapPriority: 1.0,
  changefreq: 'weekly',
  metaTitle: 'Seven Analytics | BPO Financeiro e Gestão em Tempo Real',
  metaDescription:
    'BPO Financeiro estratégico e plataforma de gestão em tempo real da Seven Analytics. Contas a pagar e receber, conciliação bancária diária e fluxo de caixa.',
  keywords: [
    'bpo financeiro',
    'seven analytics',
    'gestão financeira em tempo real',
    'conciliação bancária',
    'fluxo de caixa',
  ],
  breadcrumbs: [{ label: 'Início', path: '/' }],
  heroTag: ORIGINAL_LANDING_CONTENT.heroTag,
  h1: ORIGINAL_LANDING_CONTENT.h1,
  leadParagraph: ORIGINAL_LANDING_CONTENT.subheadline,
  geoSummaryBlock: COMMON_GEO_SUMMARY,
  primaryCtaLabel: 'Agendar demonstração',
  whatsappPrefillMessage:
    'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
  serviceScope: COMMON_SCOPE,
  processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
  deliverables: COMMON_DELIVERABLES,
  kpis: COMMON_KPIS,
  comparisonRows: COMMON_COMPARISON_ROWS,
  faqs: COMMON_FAQS,
  relatedPaths: [
    '/bpo-financeiro',
    '/plataforma-seven',
    '/quem-somos',
  ],
};

export const BPO_PILLAR_PAGE: SeoServicePage = {
  id: 'bpo-financeiro',
  path: '/bpo-financeiro',
  category: 'pillar_service',
  shortTitle: 'BPO Financeiro',
  iconName: 'BarChart3',
  targetSectionId: 'bpo-essencial',
  sitemapPriority: 1.0,
  changefreq: 'weekly',
  metaTitle: 'BPO Financeiro | Solução Personalizada — Seven Analytics',
  metaDescription:
    'Organize seu departamento financeiro com a assessoria da Seven Analytics. Contas a pagar e receber, conciliação diária e fluxo de caixa.',
  keywords: [
    'bpo financeiro',
    'terceirização financeira',
    'assessoria financeira',
    'seven analytics',
  ],
  breadcrumbs: [
    { label: 'Início', path: '/' },
    { label: 'BPO Financeiro', path: '/bpo-financeiro' },
  ],
  heroTag: ORIGINAL_LANDING_CONTENT.bpoEssencial.tag,
  h1: ORIGINAL_LANDING_CONTENT.h1,
  leadParagraph: ORIGINAL_LANDING_CONTENT.bpoEssencial.lead,
  geoSummaryBlock: COMMON_GEO_SUMMARY,
  primaryCtaLabel: 'Solicitar Orçamento Personalizado',
  whatsappPrefillMessage:
    'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
  serviceScope: COMMON_SCOPE,
  processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
  deliverables: COMMON_DELIVERABLES,
  kpis: COMMON_KPIS,
  comparisonRows: COMMON_COMPARISON_ROWS,
  faqs: COMMON_FAQS,
  relatedPaths: [
    '/bpo-financeiro/contas-a-pagar',
    '/bpo-financeiro/contas-a-receber-e-cobranca',
    '/bpo-financeiro/conciliacao-bancaria',
    '/bpo-financeiro/faturamento-e-emissao-de-notas',
    '/bpo-financeiro/fluxo-de-caixa-e-dre',
  ],
};

export const BPO_SUBSERVICE_PAGES: SeoServicePage[] = [
  {
    id: 'bpo-contas-a-pagar',
    slug: 'contas-a-pagar',
    path: '/bpo-financeiro/contas-a-pagar',
    category: 'sub_service',
    shortTitle: 'Contas a Pagar',
    iconName: 'FileCheck2',
    targetSectionId: 'bpo-essencial',
    sitemapPriority: 0.9,
    changefreq: 'monthly',
    metaTitle: 'Contas a Pagar e Receber | BPO Financeiro Seven Analytics',
    metaDescription:
      'Contas a pagar e receber de forma organizada. Cadastro em segundos, comprovante anexado e contas do mês que se repetem sozinhas na Seven.',
    keywords: ['contas a pagar', 'bpo financeiro contas a pagar', 'seven analytics'],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: 'Contas a Pagar', path: '/bpo-financeiro/contas-a-pagar' },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.bpoEssencial.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.platform.features[1].desc,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Solicitar Orçamento Personalizado',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    serviceScope: COMMON_SCOPE,
    processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
    deliverables: COMMON_DELIVERABLES,
    kpis: COMMON_KPIS,
    faqs: COMMON_FAQS,
    relatedPaths: ['/bpo-financeiro', '/bpo-financeiro/contas-a-receber-e-cobranca'],
  },
  {
    id: 'bpo-contas-a-receber',
    slug: 'contas-a-receber-e-cobranca',
    path: '/bpo-financeiro/contas-a-receber-e-cobranca',
    category: 'sub_service',
    shortTitle: 'Contas a Receber e Cobrança',
    iconName: 'Receipt',
    targetSectionId: 'bpo-essencial',
    sitemapPriority: 0.9,
    changefreq: 'monthly',
    metaTitle: 'Gestão de Cobranças e Recebíveis | BPO Financeiro Seven',
    metaDescription:
      'Gestão completa de cobranças e pagamentos. Entradas, saídas, saldo e o que falta receber atualizados no momento em que acontecem.',
    keywords: ['contas a receber', 'gestão de cobranças', 'bpo financeiro', 'seven analytics'],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: 'Contas a Receber e Cobrança', path: '/bpo-financeiro/contas-a-receber-e-cobranca' },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.bpoEssencial.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.platform.features[0].desc,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Solicitar Orçamento Personalizado',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    serviceScope: COMMON_SCOPE,
    processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
    deliverables: COMMON_DELIVERABLES,
    kpis: COMMON_KPIS,
    faqs: COMMON_FAQS,
    relatedPaths: ['/bpo-financeiro', '/bpo-financeiro/contas-a-pagar'],
  },
  {
    id: 'bpo-conciliacao-bancaria',
    slug: 'conciliacao-bancaria',
    path: '/bpo-financeiro/conciliacao-bancaria',
    category: 'sub_service',
    shortTitle: 'Conciliação Bancária',
    iconName: 'CheckCircle2',
    targetSectionId: 'plataforma',
    sitemapPriority: 0.9,
    changefreq: 'monthly',
    metaTitle: 'Conciliação Bancária Diária | BPO Financeiro Seven',
    metaDescription:
      'O extrato do banco chega, o assistente sugere a correspondência e você só confirma. Conciliações diárias e relatórios com a Seven Analytics.',
    keywords: ['conciliação bancária', 'bpo financeiro conciliação', 'seven analytics'],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: 'Conciliação Bancária', path: '/bpo-financeiro/conciliacao-bancaria' },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.platform.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.platform.features[2].desc,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Agendar demonstração',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    serviceScope: COMMON_SCOPE,
    processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
    deliverables: COMMON_DELIVERABLES,
    kpis: COMMON_KPIS,
    faqs: COMMON_FAQS,
    relatedPaths: ['/bpo-financeiro', '/bpo-financeiro/fluxo-de-caixa-e-dre'],
  },
  {
    id: 'bpo-faturamento-notas',
    slug: 'faturamento-e-emissao-de-notas',
    path: '/bpo-financeiro/faturamento-e-emissao-de-notas',
    category: 'sub_service',
    shortTitle: 'Faturamento e Emissão de Notas',
    iconName: 'FileSpreadsheet',
    targetSectionId: 'bpo-essencial',
    sitemapPriority: 0.9,
    changefreq: 'monthly',
    metaTitle: 'Faturamento e Gestão Operacional | BPO Financeiro Seven',
    metaDescription:
      'Organize e impulsione seu departamento financeiro com a assessoria da Seven Analytics. Gestão completa de cobranças, pagamentos e operação.',
    keywords: ['faturamento', 'emissão de notas', 'bpo financeiro', 'seven analytics'],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: 'Faturamento e Emissão de Notas', path: '/bpo-financeiro/faturamento-e-emissao-de-notas' },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.bpoEssencial.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.bpoEssencial.lead,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Solicitar Orçamento Personalizado',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    serviceScope: COMMON_SCOPE,
    processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
    deliverables: COMMON_DELIVERABLES,
    kpis: COMMON_KPIS,
    faqs: COMMON_FAQS,
    relatedPaths: ['/bpo-financeiro', '/bpo-financeiro/contas-a-receber-e-cobranca'],
  },
  {
    id: 'bpo-fluxo-de-caixa-e-dre',
    slug: 'fluxo-de-caixa-e-dre',
    path: '/bpo-financeiro/fluxo-de-caixa-e-dre',
    category: 'sub_service',
    shortTitle: 'Fluxo de Caixa e DRE',
    iconName: 'BarChart3',
    targetSectionId: 'plataforma',
    sitemapPriority: 0.9,
    changefreq: 'monthly',
    metaTitle: 'Fluxo de Caixa e Resultado Mensal | Seven Analytics',
    metaDescription:
      'Análise de fluxo de caixa projetado para tomada de decisão. Receita, custo, lucro e comparativo com o previsto em uma tela clara na Seven.',
    keywords: ['fluxo de caixa projetado', 'resultado do mês', 'dre', 'seven analytics'],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: 'Fluxo de Caixa e DRE', path: '/bpo-financeiro/fluxo-de-caixa-e-dre' },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.platform.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.platform.features[3].desc,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Agendar demonstração',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    serviceScope: COMMON_SCOPE,
    processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
    deliverables: COMMON_DELIVERABLES,
    kpis: COMMON_KPIS,
    faqs: COMMON_FAQS,
    relatedPaths: ['/bpo-financeiro', '/plataforma-seven'],
  },
];

export const ABOUT_US_PAGE: SeoServicePage = {
  id: 'quem-somos',
  path: '/quem-somos',
  category: 'institutional',
  shortTitle: 'Quem Somos',
  iconName: 'ShieldCheck',
  targetSectionId: 'bpo',
  sitemapPriority: 0.8,
  changefreq: 'monthly',
  metaTitle: 'Quem Somos | Seven Analytics — Inteligência Financeira',
  metaDescription:
    'A Seven organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real para empresas, contadores e escritórios de BPO.',
  keywords: ['quem somos seven analytics', 'seven analytics', 'bpo financeiro'],
  breadcrumbs: [
    { label: 'Início', path: '/' },
    { label: 'Quem Somos', path: '/quem-somos' },
  ],
  heroTag: ORIGINAL_LANDING_CONTENT.heroTag,
  h1: ORIGINAL_LANDING_CONTENT.h1,
  leadParagraph: ORIGINAL_LANDING_CONTENT.subheadline,
  geoSummaryBlock: COMMON_GEO_SUMMARY,
  primaryCtaLabel: 'Agendar demonstração',
  whatsappPrefillMessage:
    'Olá! Gostaria de falar com um especialista e solicitar uma proposta para a Seven Analytics.',
  serviceScope: COMMON_SCOPE,
  processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
  deliverables: COMMON_DELIVERABLES,
  kpis: COMMON_KPIS,
  faqs: COMMON_FAQS,
  relatedPaths: ['/bpo-financeiro', '/plataforma-seven'],
};

export const PLATFORM_PAGE: SeoServicePage = {
  id: 'plataforma-seven',
  path: '/plataforma-seven',
  category: 'platform',
  shortTitle: 'Plataforma Seven',
  iconName: 'Sparkles',
  targetSectionId: 'plataforma',
  sitemapPriority: 0.9,
  changefreq: 'monthly',
  metaTitle: 'Plataforma Seven Analytics | O Financeiro em Uma Tela',
  metaDescription:
    'Você lança uma vez e a Seven cuida do resto. Caixa em tempo real, conciliação em minutos e assistente Lity em português para sua empresa.',
  keywords: ['plataforma seven', 'seven analytics', 'assistente lity', 'planos seven'],
  breadcrumbs: [
    { label: 'Início', path: '/' },
    { label: 'Plataforma Seven', path: '/plataforma-seven' },
  ],
  heroTag: ORIGINAL_LANDING_CONTENT.platform.tag,
  h1: ORIGINAL_LANDING_CONTENT.h1,
  leadParagraph: ORIGINAL_LANDING_CONTENT.platform.lead,
  geoSummaryBlock: COMMON_GEO_SUMMARY,
  primaryCtaLabel: 'Agendar demonstração',
  whatsappPrefillMessage:
    'Olá! Gostaria de falar com um especialista e conhecer a Plataforma Seven Analytics.',
  serviceScope: COMMON_SCOPE,
  processSteps: [...ORIGINAL_LANDING_CONTENT.operation.routines],
  deliverables: COMMON_DELIVERABLES,
  kpis: COMMON_KPIS,
  comparisonRows: COMMON_COMPARISON_ROWS,
  faqs: COMMON_FAQS,
  relatedPaths: ['/bpo-financeiro', '/quem-somos'],
};

/* ============================================================================
   2. ROTAS POR SEGMENTO (USANDO EXCLUSIVAMENTE O CONTEÚDO ORIGEM DA LANDING PAGE)
   ============================================================================ */

function createSegmentFromOriginalLanding(
  slug: string,
  segmentName: string,
  iconName: string
): SeoSegmentPage {
  const path = `/bpo-financeiro-para-${slug}`;
  return {
    id: `segment-${slug}`,
    slug,
    path,
    category: 'segment',
    segmentName,
    shortDescription: ORIGINAL_LANDING_CONTENT.bpoEssencial.customProposalNote,
    iconName,
    targetSectionId: 'bpo-essencial',
    sitemapPriority: 0.8,
    changefreq: 'monthly',
    metaTitle: `BPO Financeiro para ${segmentName} | Seven`,
    metaDescription: `Organize seu departamento financeiro com a assessoria da Seven Analytics. Proposta personalizada de BPO Financeiro para ${segmentName}.`,
    keywords: [
      `bpo financeiro para ${segmentName.toLowerCase()}`,
      'bpo financeiro',
      'seven analytics',
    ],
    breadcrumbs: [
      { label: 'Início', path: '/' },
      { label: 'BPO Financeiro', path: '/bpo-financeiro' },
      { label: segmentName, path },
    ],
    heroTag: ORIGINAL_LANDING_CONTENT.bpoEssencial.tag,
    h1: ORIGINAL_LANDING_CONTENT.h1,
    leadParagraph: ORIGINAL_LANDING_CONTENT.bpoEssencial.lead,
    geoSummaryBlock: COMMON_GEO_SUMMARY,
    primaryCtaLabel: 'Solicitar Orçamento Personalizado',
    whatsappPrefillMessage:
      'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
    sectorPainPoints: ORIGINAL_LANDING_CONTENT.contrast.less.map((symptom, idx) => ({
      symptom,
      rootCause: ORIGINAL_LANDING_CONTENT.contrast.lessHeading,
      sevenSolution: ORIGINAL_LANDING_CONTENT.contrast.more[idx] || '',
    })),
    specificRoutines: ORIGINAL_LANDING_CONTENT.platform.features.map((f) => ({
      title: f.title,
      description: f.desc,
    })),
    sectorIndicators: COMMON_KPIS,
    faqs: COMMON_FAQS,
    recommendedServicePaths: [
      '/bpo-financeiro',
      '/bpo-financeiro/contas-a-pagar',
      '/bpo-financeiro/conciliacao-bancaria',
      '/bpo-financeiro/fluxo-de-caixa-e-dre',
    ],
    relatedPaths: ['/bpo-financeiro', '/plataforma-seven'],
  };
}

export const BPO_SEGMENT_PAGES: SeoSegmentPage[] = [
  createSegmentFromOriginalLanding('clinicas', 'Clínicas', 'Stethoscope'),
  createSegmentFromOriginalLanding('restaurantes', 'Restaurantes', 'Utensils'),
  createSegmentFromOriginalLanding('e-commerce', 'E-commerce', 'ShoppingBag'),
  createSegmentFromOriginalLanding('startups', 'Startups', 'Rocket'),
  createSegmentFromOriginalLanding('escritorios-de-advocacia', 'Escritórios de Advocacia', 'Scale'),
  createSegmentFromOriginalLanding('construtoras', 'Construtoras', 'HardHat'),
  createSegmentFromOriginalLanding('franquias', 'Franquias', 'Store'),
];

/* ============================================================================
   3. ÍNDICE DE CONTEÚDO / BLOG (USANDO CONTEÚDO ORIGEM DA LANDING PAGE)
   ============================================================================ */

export const BLOG_INDEX_PAGE: SeoPageDefinition = {
  id: 'blog-index',
  path: '/blog',
  category: 'blog_index',
  targetSectionId: 'plataforma',
  sitemapPriority: 0.8,
  changefreq: 'weekly',
  metaTitle: 'Conteúdo sobre BPO e Gestão Financeira | Seven Analytics',
  metaDescription:
    'Menos planilha, mais decisão. A Seven organiza o seu financeiro, cuida da operação e mostra o resultado da sua empresa em tempo real.',
  keywords: ['seven analytics', 'bpo financeiro', 'gestão financeira'],
  breadcrumbs: [
    { label: 'Início', path: '/' },
    { label: 'Conteúdo', path: '/blog' },
  ],
  heroTag: ORIGINAL_LANDING_CONTENT.heroTag,
  h1: ORIGINAL_LANDING_CONTENT.h1,
  leadParagraph: ORIGINAL_LANDING_CONTENT.subheadline,
  geoSummaryBlock: COMMON_GEO_SUMMARY,
  primaryCtaLabel: 'Agendar demonstração',
  whatsappPrefillMessage:
    'Olá! Gostaria de agendar um diagnóstico e solicitar uma proposta de BPO Financeiro com a Seven Analytics.',
  faqs: COMMON_FAQS,
  relatedPaths: ['/bpo-financeiro', '/plataforma-seven'],
};

export const BLOG_POSTS: SeoBlogPost[] = [];

export const ALL_SEO_PAGES: SeoPageDefinition[] = [
  HOME_SEO_METADATA,
  BPO_PILLAR_PAGE,
  ...BPO_SUBSERVICE_PAGES,
  ABOUT_US_PAGE,
  PLATFORM_PAGE,
  ...BPO_SEGMENT_PAGES,
  BLOG_INDEX_PAGE,
];

export function findSeoPageByPath(pathname: string): SeoPageDefinition | undefined {
  const normalized = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
  return ALL_SEO_PAGES.find((p) => p.path === normalized);
}

export const getSeoPageByPath = findSeoPageByPath;

export function findSubserviceBySlug(slug: string): SeoServicePage | undefined {
  return BPO_SUBSERVICE_PAGES.find((p) => p.slug === slug);
}

export const getBpoSubserviceBySlug = findSubserviceBySlug;

export function findSegmentByPath(pathname: string): SeoSegmentPage | undefined {
  const normalized = pathname.replace(/\/+$/, '');
  return BPO_SEGMENT_PAGES.find((p) => p.path === normalized);
}

export const getBpoSegmentByPath = findSegmentByPath;

export function findBlogPostBySlug(slug: string): SeoBlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

export const getBlogPostBySlug = findBlogPostBySlug;

export function buildPageJsonLd(page: SeoPageDefinition): Record<string, unknown> {
  const canonicalUrl = `${CANONICAL_DOMAIN}${page.path === '/' ? '/' : page.path}`;
  const graph: Array<Record<string, unknown>> = [
    {
      '@type': 'WebSite',
      '@id': `${CANONICAL_DOMAIN}/#website`,
      url: `${CANONICAL_DOMAIN}/`,
      name: 'Seven Analytics',
      alternateName: ['Seven BPO Financeiro', 'Seven BPO', 'sevenbpo.com.br'],
      publisher: { '@id': `${CANONICAL_DOMAIN}/#organization` },
      inLanguage: 'pt-BR',
    },
    {
      '@type': ['Organization', 'FinancialService'],
      '@id': `${CANONICAL_DOMAIN}/#organization`,
      name: 'Seven Analytics',
      alternateName: 'Seven BPO Financeiro',
      url: `${CANONICAL_DOMAIN}/`,
      description: page.metaDescription,
      areaServed: {
        '@type': 'Country',
        name: 'Brasil',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'contato@sevenbpo.com.br',
        availableLanguage: ['Portuguese'],
      },
    },
    {
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: page.metaTitle,
      description: page.metaDescription,
      isPartOf: { '@id': `${CANONICAL_DOMAIN}/#website` },
      about: { '@id': `${CANONICAL_DOMAIN}/#organization` },
      inLanguage: 'pt-BR',
    },
  ];

  graph.push({
    '@type': page.category === 'platform' ? 'SoftwareApplication' : 'Service',
    '@id': `${canonicalUrl}#service`,
    name: page.metaTitle,
    description: page.metaDescription,
    url: canonicalUrl,
    provider: { '@id': `${CANONICAL_DOMAIN}/#organization` },
    areaServed: 'BR',
  });

  if (page.breadcrumbs && page.breadcrumbs.length > 0) {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: page.breadcrumbs.map((crumb, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        name: crumb.label,
        item: `${CANONICAL_DOMAIN}${crumb.path === '/' ? '' : crumb.path}`,
      })),
    });
  }

  if (page.faqs && page.faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

export function generateSitemapXml(lastmod = '2026-10-15'): string {
  const urls = [
    ...ALL_SEO_PAGES.map((p) => ({
      loc: `${CANONICAL_DOMAIN}${p.path === '/' ? '/' : p.path}`,
      lastmod,
      changefreq: p.changefreq,
      priority: p.sitemapPriority.toFixed(1),
    })),
    {
      loc: `${CANONICAL_DOMAIN}/contato`,
      lastmod,
      changefreq: 'monthly',
      priority: '0.8',
    },
  ];

  const entries = urls
    .map(
      (u) => `  <url>
    <loc>${u.loc}</loc>
    <lastmod>${u.lastmod}</lastmod>
    <changefreq>${u.changefreq}</changefreq>
    <priority>${u.priority}</priority>
  </url>`
    )
    .join('\n');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${entries}
</urlset>
`;
}

export function generateRobotsTxt(): string {
  return `User-agent: *
Allow: /
Allow: /bpo-financeiro
Allow: /bpo-financeiro/
Allow: /quem-somos
Allow: /plataforma-seven
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/
Disallow: /login
Disallow: /register
Disallow: /forgot-password
Disallow: /reset-password
Disallow: /onboarding

User-agent: Googlebot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: Bingbot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: GPTBot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: OAI-SearchBot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: ChatGPT-User
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: PerplexityBot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: ClaudeBot
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: anthropic-ai
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

User-agent: Google-Extended
Allow: /
Disallow: /dashboard
Disallow: /admin
Disallow: /partner
Disallow: /api/

Sitemap: ${CANONICAL_DOMAIN}/sitemap.xml
`;
}

export function generateLlmsTxt(): string {
  return `# Seven Analytics — BPO Financeiro e Plataforma Financeira em Tempo Real

> A Seven organiza o seu financeiro, cuida da operação e mostra o resultado em tempo real. Você para de passar o dia amarrado no operacional — e volta a decidir com clareza. Para empresas, contadores e escritórios que cuidam de vários clientes.

## Páginas Principais e Serviços de BPO Financeiro

- [Seven Analytics — Início](${CANONICAL_DOMAIN}/): Plataforma financeira em tempo real e BPO Financeiro estratégico para empresas, contadores e escritórios.
- [BPO Financeiro — Solução Personalizada](${CANONICAL_DOMAIN}/bpo-financeiro): Terceirização e organização do departamento financeiro com contas a pagar, contas a receber, conciliação diária e fluxo de caixa.
- [Contas a Pagar e Receber](${CANONICAL_DOMAIN}/bpo-financeiro/contas-a-pagar): Cadastro em segundos, comprovante anexado com um arraste e contas recorrentes do mês.
- [Gestão de Cobranças e Recebíveis](${CANONICAL_DOMAIN}/bpo-financeiro/contas-a-receber-e-cobranca): Entradas, saídas, saldo e recebíveis atualizados no momento em que acontecem.
- [Conciliação Bancária Diária](${CANONICAL_DOMAIN}/bpo-financeiro/conciliacao-bancaria): O extrato do banco chega, o assistente sugere a correspondência e você só confirma.
- [Faturamento e Gestão Operacional](${CANONICAL_DOMAIN}/bpo-financeiro/faturamento-e-emissao-de-notas): Gestão completa de cobranças, pagamentos e rotina financeira empresarial.
- [Fluxo de Caixa e Resultado Mensal (DRE)](${CANONICAL_DOMAIN}/bpo-financeiro/fluxo-de-caixa-e-dre): Receita, custo, lucro e comparativo com o previsto em uma tela clara.
- [Plataforma Seven Analytics](${CANONICAL_DOMAIN}/plataforma-seven): Caixa em tempo real, conciliação em minutos, operação multi-empresa e assistente Lity em português.
- [Quem Somos — Seven Analytics](${CANONICAL_DOMAIN}/quem-somos): Inteligência financeira e controle operacional completo para prestadores de serviços, contadores e BPOs.
- [Conteúdo sobre BPO e Gestão Financeira](${CANONICAL_DOMAIN}/blog): Artigos e materiais sobre organização financeira, fluxo de caixa e tomada de decisão.

## BPO Financeiro por Segmento

- [BPO Financeiro para Clínicas](${CANONICAL_DOMAIN}/bpo-financeiro-para-clinicas): Organização financeira, conciliação diária e fluxo de caixa para clínicas e consultórios.
- [BPO Financeiro para Restaurantes](${CANONICAL_DOMAIN}/bpo-financeiro-para-restaurantes): Gestão de contas a pagar, conciliação diária e resultado mensal para restaurantes e bares.
- [BPO Financeiro para E-commerce](${CANONICAL_DOMAIN}/bpo-financeiro-para-e-commerce): Controle de entradas, saídas, conciliação bancária e fluxo de caixa para operações digitais.
- [BPO Financeiro para Startups](${CANONICAL_DOMAIN}/bpo-financeiro-para-startups): Visão de caixa em tempo real, relatórios de desempenho e gestão financeira enxuta para startups.
- [BPO Financeiro para Escritórios de Advocacia](${CANONICAL_DOMAIN}/bpo-financeiro-para-escritorios-de-advocacia): Controle de recebíveis, despesas operacionais e relatórios claros para bancas jurídicas.
- [BPO Financeiro para Construtoras](${CANONICAL_DOMAIN}/bpo-financeiro-para-construtoras): Organização de pagamentos, conciliação bancária e visão multi-empresa para construtoras e engenharias.
- [BPO Financeiro para Franquias](${CANONICAL_DOMAIN}/bpo-financeiro-para-franquias): Painel único multi-CNPJ para acompanhar o resultado de múltiplas unidades franqueadas.

## Documentação e Descoberta Agêntica

- [Mapa do Site XML](${CANONICAL_DOMAIN}/sitemap.xml): Índice estruturado de todas as páginas públicas da Seven Analytics.
- [Catálogo de Recursos Agênticos (ARD)](${CANONICAL_DOMAIN}/.well-known/ai-catalog.json): Manifesto Agentic Resource Discovery da Seven Analytics.

## Contato Oficial

- E-mail: contato@sevenbpo.com.br
- Telefone / WhatsApp: +55 11 91633-8874
- Atendimento: Seg. a Sex., 9h às 18h
`;
}
